import { z } from 'zod';
import { blankToNull } from './blankToNull';

// A «Usuario» delivery as the panel renders it in the «Entregas» card. The wire shape
// is snake_case (app-service / DRF convention); this schema parses it and exposes a
// camelCase domain object. `status` and `carrier` are open strings, never enums: a new
// backend state must not break parsing — the UI maps known states to a colour and
// degrades the rest to neutral. `tracking` and `carrier` are optional: a delivery may
// arrive without them, and the row renders "—" rather than failing the whole card.
// `boxNumber` is nullable for the same reason: a delivery not currently occupying a box
// (before assignment or after release) arrives with `box_number: null`, and one such row
// must not fail the parse of the whole list. `lockerTimezone` is the IANA zone the panel
// formats the dates in (locker-local). `locationAddress` is the address of the BUILDING the
// delivery was made in — its main portal, the same string the delivery detail serves — and it
// is «Usuario»-adjacent PII: render, never log.
export const DeliverySchema = z
  .object({
    id: z.string().min(1),
    stored_date: z.string(),
    collected_date: z.string().nullable(),
    status: z.string(),
    box_number: z.string().nullable(),
    tracking: z.string().nullish(),
    carrier: z.string().nullish(),
    shared_with_recipient: z.boolean(),
    locker_timezone: z.string(),
    // `nullish` on the OBJECT is about the DEPLOY, not about the contract: backend serves the
    // object on every row, but the response live in production right now predates it and omits
    // the key entirely — `nullish` is what keeps that response parsing instead of blanking the
    // whole list, and it is why the clipboard column can ship before the deploy.
    //
    // `nullish` INSIDE is the contract: `address` is null for a building with no address row,
    // one whose main portal composes to an empty string, an unreadable building, and a delivery
    // that never reached one. Four wire spellings of «no address on file», one fact.
    //
    // One member, deliberately: no `id` (no consumer here) and no `timezone` — the row already
    // carries `locker_timezone` flat, and one row must not state the same fact twice.
    location: z.object({ address: z.string().nullish() }).nullish(),
  })
  .transform((d) => ({
    id: d.id,
    storedDate: d.stored_date,
    collectedDate: d.collected_date,
    status: d.status,
    boxNumber: d.box_number,
    tracking: d.tracking ?? null,
    carrier: d.carrier ?? null,
    sharedWithRecipient: d.shared_with_recipient,
    lockerTimezone: d.locker_timezone,
    // FLATTENED, unlike the wire. The object has ONE member, so «no location key» and «address
    // null» mean the very same thing — no address on file — and collapsing them leaves ONE
    // representable absence, which is what lets every consumer write `?? '—'` without
    // re-deriving the question. The sibling row schema keeps `recipient` nested for the opposite
    // reason: there the two absences are different facts.
    locationAddress: blankToNull(d.location?.address),
  }));

// The card shows at most five recent deliveries plus the 12-month count; the count
// drives the badge and is computed server-side (never from the truncated list).
export const UserDeliveriesResponseSchema = z.object({
  count: z.number(),
  deliveries: z.array(DeliverySchema),
});

export type Delivery = z.infer<typeof DeliverySchema>;
export type UserDeliveriesResponse = z.infer<typeof UserDeliveriesResponseSchema>;
